import type { Category } from '@/types'

export interface SubcategoryConfig {
  label: string
  fields: FieldConfig[]
}

export interface FieldConfig {
  key: string
  label: string
  type: 'text' | 'select' | 'number' | 'date' | 'checkbox'
  options?: string[]
  placeholder?: string
  metadataField?: boolean
}

export interface CategoryConfig {
  label: string
  icon: string
  color: string
  bgColor: string
  borderColor: string
  subcategories: Record<string, SubcategoryConfig>
}

export const CATEGORIES: Record<Category, CategoryConfig> = {

  // ─── Vehicles ───────────────────────────────────────────────────────────────
  vehicle: {
    label: 'Vehicles',
    icon: 'Car',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    subcategories: {
      vehicle_details: {
        label: 'Vehicle Details',
        fields: [
          { key: 'make',         label: 'Make',          type: 'text', placeholder: 'e.g. Toyota' },
          { key: 'model',        label: 'Model',         type: 'text', placeholder: 'e.g. RAV4' },
          { key: 'year',         label: 'Year',          type: 'number', placeholder: '2022', metadataField: true },
          { key: 'colour',       label: 'Colour',        type: 'text', metadataField: true },
          { key: 'body_type',    label: 'Body Type',     type: 'select', options: ['Sedan','Hatchback','SUV','Ute','Van','Wagon','Coupe','Convertible','Other'], metadataField: true },
          { key: 'vin',          label: 'VIN',           type: 'text', metadataField: true },
          { key: 'engine_no',    label: 'Engine Number', type: 'text', metadataField: true },
          { key: 'odometer',     label: 'Odometer (km)', type: 'number', metadataField: true },
        ],
      },
      registration: {
        label: 'Registration',
        fields: [
          { key: 'plate',          label: 'Plate Number',    type: 'text', placeholder: 'e.g. ABC123' },
          { key: 'state',          label: 'State',           type: 'select', options: ['ACT','NSW','NT','QLD','SA','TAS','VIC','WA'], metadataField: true },
          { key: 'vehicle_ref',    label: 'Vehicle (ref)',   type: 'text', placeholder: 'e.g. 2022 Toyota RAV4', metadataField: true },
          { key: 'ctp_insurer',    label: 'CTP Insurer',     type: 'text', metadataField: true },
          { key: 'ctp_policy',     label: 'CTP Policy #',    type: 'text', metadataField: true },
          { key: 'ctp_expiry',     label: 'CTP Expiry',      type: 'date', metadataField: true },
        ],
      },
      vehicle_insurance: {
        label: 'Vehicle Insurance',
        fields: [
          { key: 'vehicle_ref',  label: 'Vehicle (ref)',     type: 'text', placeholder: 'e.g. 2022 Toyota RAV4', metadataField: true },
          { key: 'cover_type',   label: 'Cover Type',        type: 'select', options: ['Comprehensive','Third Party Property','Third Party Fire & Theft'], metadataField: true },
          { key: 'excess',       label: 'Excess',            type: 'number', metadataField: true },
          { key: 'sum_insured',  label: 'Sum Insured',       type: 'number', metadataField: true },
        ],
      },
      service: {
        label: 'Service / Maintenance',
        fields: [
          { key: 'vehicle_ref',      label: 'Vehicle (ref)',      type: 'text', placeholder: 'e.g. 2022 Toyota RAV4', metadataField: true },
          { key: 'last_service_date', label: 'Last Service Date', type: 'date', metadataField: true },
          { key: 'last_service_km',  label: 'Last Service (km)',  type: 'number', metadataField: true },
          { key: 'next_service_km',  label: 'Next Service (km)',  type: 'number', metadataField: true },
          { key: 'mechanic',         label: 'Mechanic / Dealer',  type: 'text', metadataField: true },
        ],
      },
      roadside: {
        label: 'Roadside Assistance',
        fields: [
          { key: 'club',             label: 'Club',             type: 'select', options: ['NRMA','RACV','RAA','RAQ','RAC','RACT','AANT','Other'], metadataField: true },
          { key: 'membership_level', label: 'Membership Level', type: 'text', placeholder: 'e.g. Premium', metadataField: true },
          { key: 'member_number',    label: 'Member Number',    type: 'text', metadataField: true },
        ],
      },
    },
  },

  // ─── Home ────────────────────────────────────────────────────────────────────
  home: {
    label: 'Home',
    icon: 'Home',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    subcategories: {
      home_building_insurance: {
        label: 'Home Building Insurance',
        fields: [
          { key: 'address',      label: 'Property Address', type: 'text', metadataField: true },
          { key: 'sum_insured',  label: 'Sum Insured',      type: 'number', metadataField: true },
          { key: 'excess',       label: 'Excess',           type: 'number', metadataField: true },
        ],
      },
      home_contents_insurance: {
        label: 'Home Contents Insurance',
        fields: [
          { key: 'address',      label: 'Property Address', type: 'text', metadataField: true },
          { key: 'sum_insured',  label: 'Sum Insured',      type: 'number', metadataField: true },
          { key: 'excess',       label: 'Excess',           type: 'number', metadataField: true },
        ],
      },
      mortgage: {
        label: 'Mortgage',
        fields: [
          { key: 'address',           label: 'Property Address',  type: 'text', metadataField: true },
          { key: 'account_number',    label: 'Account Number',    type: 'text', metadataField: true },
          { key: 'rate_type',         label: 'Rate Type',         type: 'select', options: ['Variable','Fixed','Split'], metadataField: true },
          { key: 'interest_rate',     label: 'Interest Rate (%)', type: 'number', metadataField: true },
          { key: 'fixed_rate_expiry', label: 'Fixed Rate Expiry', type: 'date', metadataField: true },
          { key: 'loan_balance',      label: 'Loan Balance',      type: 'number', metadataField: true },
        ],
      },
      council_rates: {
        label: 'Council Rates',
        fields: [
          { key: 'address',           label: 'Property Address',  type: 'text', metadataField: true },
          { key: 'council',           label: 'Council Name',      type: 'text', metadataField: true },
          { key: 'assessment_number', label: 'Assessment Number', type: 'text', metadataField: true },
        ],
      },
      water_rates: {
        label: 'Water Rates',
        fields: [
          { key: 'address',        label: 'Property Address', type: 'text', metadataField: true },
          { key: 'account_number', label: 'Account Number',   type: 'text', metadataField: true },
        ],
      },
      strata_levy: {
        label: 'Strata Levy',
        fields: [
          { key: 'address',        label: 'Property Address', type: 'text', metadataField: true },
          { key: 'lot_number',     label: 'Lot Number',       type: 'text', metadataField: true },
          { key: 'strata_manager', label: 'Strata Manager',   type: 'text', metadataField: true },
        ],
      },
      warranty: {
        label: 'Appliance / Product Warranty',
        fields: [
          { key: 'product',       label: 'Product',       type: 'text', placeholder: 'e.g. Samsung Washer' },
          { key: 'model_number',  label: 'Model Number',  type: 'text', metadataField: true },
          { key: 'purchase_date', label: 'Purchase Date', type: 'date', metadataField: true },
          { key: 'retailer',      label: 'Retailer',      type: 'text', metadataField: true },
        ],
      },
    },
  },

  // ─── Property Investments ────────────────────────────────────────────────────
  property: {
    label: 'Property',
    icon: 'Building2',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    subcategories: {
      investment_property: {
        label: 'Property Details',
        fields: [
          { key: 'address',         label: 'Property Address',    type: 'text', metadataField: true },
          { key: 'property_type',   label: 'Property Type',       type: 'select', options: ['House','Apartment/Unit','Townhouse','Land','Commercial','Other'], metadataField: true },
          { key: 'purchase_date',   label: 'Purchase Date',       type: 'date', metadataField: true },
          { key: 'purchase_price',  label: 'Purchase Price',      type: 'number', metadataField: true },
          { key: 'current_value',   label: 'Current Est. Value',  type: 'number', metadataField: true },
          { key: 'agent',           label: 'Selling Agent',       type: 'text', metadataField: true },
          { key: 'mortgage_lender', label: 'Mortgage Lender',     type: 'text', metadataField: true },
          { key: 'loan_balance',    label: 'Loan Balance',        type: 'number', metadataField: true },
        ],
      },
      lease: {
        label: 'Lease / Tenancy',
        fields: [
          { key: 'address',           label: 'Property Address',       type: 'text', metadataField: true },
          { key: 'tenant_name',       label: 'Tenant Name(s)',         type: 'text', metadataField: true },
          { key: 'rent_amount',       label: 'Weekly Rent',            type: 'number', metadataField: true },
          { key: 'bond_amount',       label: 'Bond Amount',            type: 'number', metadataField: true },
          { key: 'property_manager',  label: 'Property Manager',       type: 'text', metadataField: true },
          { key: 'pm_phone',          label: 'Property Manager Phone', type: 'text', metadataField: true },
          { key: 'pm_email',          label: 'Property Manager Email', type: 'text', metadataField: true },
        ],
      },
      landlord_insurance: {
        label: 'Landlord Insurance',
        fields: [
          { key: 'address',      label: 'Property Address', type: 'text', metadataField: true },
          { key: 'sum_insured',  label: 'Sum Insured',      type: 'number', metadataField: true },
          { key: 'excess',       label: 'Excess',           type: 'number', metadataField: true },
          { key: 'covers_rent',  label: 'Covers Rent Loss', type: 'select', options: ['Yes','No'], metadataField: true },
        ],
      },
      property_rates: {
        label: 'Council / Water Rates',
        fields: [
          { key: 'address',    label: 'Property Address', type: 'text', metadataField: true },
          { key: 'rate_type',  label: 'Rate Type',        type: 'select', options: ['Council Rates','Water Rates','Land Tax','Strata Levy'], metadataField: true },
          { key: 'council',    label: 'Council / Authority', type: 'text', metadataField: true },
          { key: 'account_no', label: 'Account / Assessment #', type: 'text', metadataField: true },
        ],
      },
    },
  },

  // ─── Shares ──────────────────────────────────────────────────────────────────
  shares: {
    label: 'Shares',
    icon: 'TrendingUp',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    subcategories: {
      shareholding: {
        label: 'Shares / ETF',
        fields: [
          { key: 'asx_code',       label: 'ASX / Ticker Code',   type: 'text', placeholder: 'e.g. VAS, CBA', metadataField: true },
          { key: 'security_type',  label: 'Security Type',       type: 'select', options: ['Australian Share','ETF','LIC','International Share','Managed Fund','Other'], metadataField: true },
          { key: 'quantity',       label: 'Quantity',            type: 'number', metadataField: true },
          { key: 'buy_price',      label: 'Buy Price per Share', type: 'number', metadataField: true },
          { key: 'brokerage',      label: 'Brokerage Paid',      type: 'number', metadataField: true },
          { key: 'purchase_date',  label: 'Purchase Date',       type: 'date', metadataField: true },
          { key: 'broker',         label: 'Broker / Platform',   type: 'text', placeholder: 'e.g. CommSec, SelfWealth', metadataField: true },
          { key: 'registry',       label: 'Share Registry',      type: 'select', options: ['Computershare','Link Market Services','Automic','Boardroom','Security Transfer Australia','Other'], metadataField: true },
          { key: 'holder_number',  label: 'Holder / SRN Number', type: 'text', metadataField: true },
          { key: 'chess_sponsored', label: 'CHESS Sponsored',    type: 'select', options: ['Yes','No'], metadataField: true },
          { key: 'drp',            label: 'DRP Enrolled',        type: 'select', options: ['Yes','No'], metadataField: true },
          { key: 'current_price',  label: 'Current Price (manual)', type: 'number', metadataField: true },
        ],
      },
      crypto: {
        label: 'Cryptocurrency',
        fields: [
          { key: 'coin',          label: 'Coin / Token',    type: 'text', placeholder: 'e.g. BTC, ETH' },
          { key: 'quantity',      label: 'Quantity',        type: 'number', metadataField: true },
          { key: 'buy_price_aud', label: 'Buy Price (AUD)', type: 'number', metadataField: true },
          { key: 'purchase_date', label: 'Purchase Date',   type: 'date', metadataField: true },
          { key: 'exchange',      label: 'Exchange',        type: 'text', placeholder: 'e.g. CoinSpot, Binance', metadataField: true },
          { key: 'wallet',        label: 'Wallet / Custody', type: 'text', metadataField: true },
        ],
      },
    },
  },

  // ─── Tax ─────────────────────────────────────────────────────────────────────
  tax: {
    label: 'Tax',
    icon: 'FileText',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    subcategories: {
      tax_return: {
        label: 'Tax Return',
        fields: [
          { key: 'financial_year',     label: 'Financial Year',        type: 'text', placeholder: 'e.g. 2024–25' },
          { key: 'lodgement_method',   label: 'Lodgement Method',      type: 'select', options: ['myTax (self)','Tax Agent','Accountant'], metadataField: true },
          { key: 'accountant',         label: 'Accountant / Agent',    type: 'text', metadataField: true },
          { key: 'lodgement_date',     label: 'Date Lodged',           type: 'date', metadataField: true },
          { key: 'status',             label: 'Status',                type: 'select', options: ['Pending','Lodged','Assessed'], metadataField: true },
          { key: 'refund_amount',      label: 'Refund Amount',         type: 'number', metadataField: true },
          { key: 'liability_amount',   label: 'Tax Liability',         type: 'number', metadataField: true },
          { key: 'gross_income',       label: 'Gross Income',          type: 'number', metadataField: true },
          { key: 'tax_withheld',       label: 'Tax Withheld (PAYG)',   type: 'number', metadataField: true },
        ],
      },
      tax_receipt: {
        label: 'Tax Receipt / Deduction',
        fields: [
          { key: 'financial_year',   label: 'Financial Year',     type: 'text', placeholder: 'e.g. 2024–25' },
          { key: 'deduction_type',   label: 'Deduction Type',     type: 'select', options: ['Work-related expenses','Home office','Vehicle','Self-education','Donations','Investment','Other'], metadataField: true },
          { key: 'description',      label: 'Description',        type: 'text', placeholder: 'e.g. Laptop for work', metadataField: true },
          { key: 'gst_amount',       label: 'GST Amount',         type: 'number', metadataField: true },
        ],
      },
      bas: {
        label: 'BAS',
        fields: [
          { key: 'period',      label: 'Period',      type: 'text', placeholder: 'e.g. Q1 2025', metadataField: true },
          { key: 'abn',         label: 'ABN',         type: 'text', metadataField: true },
          { key: 'gst_amount',  label: 'GST Amount',  type: 'number', metadataField: true },
          { key: 'status',      label: 'Status',      type: 'select', options: ['Pending','Lodged','Paid'], metadataField: true },
        ],
      },
      super: {
        label: 'Superannuation',
        fields: [
          { key: 'fund_name',     label: 'Fund Name',      type: 'text' },
          { key: 'member_number', label: 'Member Number',  type: 'text', metadataField: true },
          { key: 'balance',       label: 'Current Balance', type: 'number', metadataField: true },
          { key: 'employer',      label: 'Employer',        type: 'text', metadataField: true },
          { key: 'abn',           label: 'Fund ABN',        type: 'text', metadataField: true },
        ],
      },
    },
  },

  // ─── Personal Insurance ──────────────────────────────────────────────────────
  insurance: {
    label: 'Insurance',
    icon: 'Shield',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    subcategories: {
      health: {
        label: 'Health Insurance',
        fields: [
          { key: 'fund',             label: 'Health Fund',      type: 'text', placeholder: 'e.g. Medibank, Bupa' },
          { key: 'membership_number', label: 'Membership #',   type: 'text', metadataField: true },
          { key: 'tier',             label: 'Cover Tier',       type: 'select', options: ['Basic','Bronze','Silver','Gold'], metadataField: true },
          { key: 'hospital_excess',  label: 'Hospital Excess',  type: 'number', metadataField: true },
          { key: 'includes_extras',  label: 'Includes Extras',  type: 'select', options: ['Yes','No'], metadataField: true },
        ],
      },
      life: {
        label: 'Life Insurance',
        fields: [
          { key: 'sum_insured',  label: 'Sum Insured',   type: 'number', metadataField: true },
          { key: 'beneficiary',  label: 'Beneficiary',   type: 'text', metadataField: true },
          { key: 'inside_super', label: 'Inside Super',  type: 'select', options: ['Yes','No'], metadataField: true },
        ],
      },
      income_protection: {
        label: 'Income Protection',
        fields: [
          { key: 'benefit_amount',  label: 'Monthly Benefit',  type: 'number', metadataField: true },
          { key: 'benefit_period',  label: 'Benefit Period',   type: 'text', placeholder: 'e.g. 2 years', metadataField: true },
          { key: 'waiting_period',  label: 'Waiting Period',   type: 'text', placeholder: 'e.g. 90 days', metadataField: true },
          { key: 'inside_super',    label: 'Inside Super',     type: 'select', options: ['Yes','No'], metadataField: true },
        ],
      },
      travel: {
        label: 'Travel Insurance',
        fields: [
          { key: 'destination',    label: 'Destination',   type: 'text', metadataField: true },
          { key: 'departure_date', label: 'Departure',     type: 'date', metadataField: true },
          { key: 'return_date',    label: 'Return',        type: 'date', metadataField: true },
          { key: 'cover_type',     label: 'Cover Type',    type: 'select', options: ['Single trip','Annual multi-trip','Backpacker'], metadataField: true },
        ],
      },
      pet: {
        label: 'Pet Insurance',
        fields: [
          { key: 'pet_name', label: 'Pet Name', type: 'text', metadataField: true },
          { key: 'species',  label: 'Species',  type: 'select', options: ['Dog','Cat','Other'], metadataField: true },
          { key: 'breed',    label: 'Breed',    type: 'text', metadataField: true },
          { key: 'excess',   label: 'Excess',   type: 'number', metadataField: true },
        ],
      },
    },
  },

  // ─── Other ───────────────────────────────────────────────────────────────────
  other: {
    label: 'Other',
    icon: 'FolderOpen',
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    subcategories: {
      general: { label: 'General', fields: [] },
    },
  },
}

export const CATEGORY_ORDER: Category[] = ['vehicle', 'home', 'property', 'shares', 'tax', 'insurance', 'other']
